import { useEffect, useState } from 'react';
import { Menu } from '@mantine/core';
import { IconUnlink } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  PartnerViewExtensions,
  type PartnerViewPayload,
} from '@ayocore/erp/crm/client/components/Partners/views/PartnerViewExtensions';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { CRM_ROLES } from '@ayocore/erp/crm/common/roles';
import { IDENTITY_ROLES } from '@ayocore/platform/identity/common/roles';
import { unlinkPartnerFrom1c, hasPartner1cLink } from '@ayocore/grasias/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * Пункт меню «Отвязать 1С» — удаляет все записи в `crm_partners_external_refs`
 * с `external_system='1c'` для текущего партнёра.
 *
 * Видно только владельцу CRM/админу и только если у партнёра реально есть
 * привязка к 1С. Серверная проверка роли дублируется в `unlinkPartnerFrom1c`
 * (server-side RBAC).
 */
function UnlinkOneCMenuItem({ partner, reload }: PartnerViewPayload) {
  const confirm = useConfirm();
  const userRoles = useAuthController(s => s.user?.roles ?? []);
  const isOwnerOrAdmin =
    userRoles.includes(CRM_ROLES.CRM_OWNER) || userRoles.includes(IDENTITY_ROLES.ADMIN);
  const [running, setRunning] = useState(false);
  const [hasLink, setHasLink] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isOwnerOrAdmin) return;
    let cancelled = false;
    hasPartner1cLink(partner.id)
      .then(res => {
        if (!cancelled) setHasLink(res.hasLink);
      })
      .catch(() => {
        if (!cancelled) setHasLink(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partner.id, isOwnerOrAdmin]);

  if (!isOwnerOrAdmin) return null;
  if (!hasLink) return null;

  const handleClick = async () => {
    const ok = await confirm(
      `Удалить связи 1С у партнёра «${partner.name}»? После этого следующие документы из 1С с тем же guid_partner будут импортироваться без автоматической привязки.`,
      { title: 'Отвязать 1С', confirmLabel: 'Отвязать' }
    );
    if (!ok) return;

    try {
      setRunning(true);
      const { deleted } = await unlinkPartnerFrom1c(partner.id);
      notifications.show({
        color: deleted > 0 ? 'green' : 'yellow',
        message: deleted > 0 ? `Удалено связей 1С: ${deleted}` : 'Связей 1С у партнёра не было',
      });
      setHasLink(false);
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка отвязки от 1С';
      notifications.show({ title: 'Ошибка', message, color: 'red' });
      logger.error(err, '[grasias] unlinkPartnerFrom1c failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Menu.Item
      leftSection={<IconUnlink size={14} />}
      onClick={() => void handleClick()}
      disabled={running}
    >
      {running ? 'Отвязка…' : 'Отвязать 1С'}
    </Menu.Item>
  );
}

export function registerGrasiasPartnerViewExtensions(): void {
  PartnerViewExtensions.menuItems.register(payload => <UnlinkOneCMenuItem {...payload} />);
}
